import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { HardHat, MessageCircle, ShieldCheck, Users, CheckCircle2, ArrowRight } from 'lucide-react';
import whyUsImg from '@/assets/hero-construction.jpg';

const NAVY = '#071936';
const CARD_BG = '#0F2A4D';
const ACCENT = '#FFAA00';
const BODY = '#B7C3D6';
const BORDER = 'rgba(255,255,255,0.1)';

const features = [
  {
    icon: HardHat,
    title: 'Quality Construction',
    desc: 'Built with attention to detail and using premium materials for lasting results.',
  },
  {
    icon: MessageCircle,
    title: 'Transparent Communication',
    desc: 'Clear and honest communication from consultation to project completion.',
  },
  {
    icon: ShieldCheck,
    title: 'Safety First',
    desc: 'Safety-conscious planning and site management at every stage.',
  },
  {
    icon: Users,
    title: 'Experienced Team',
    desc: 'Skilled professionals working together to deliver your vision.',
  },
];

const trustStrip = ['Quality Materials', 'Skilled Team', 'Safety Standards', 'Project Management'];

export function WhyChooseSection() {
  return (
    <section className="relative py-10 md:py-12" style={{ backgroundColor: NAVY }}>
      <div className="max-w-350 mx-auto px-4 sm:px-6 lg:px-10">
        <div className="grid lg:grid-cols-[40fr_60fr] gap-8 lg:gap-12 items-center">
          {/* ── Left — Image ── */}
          <motion.div
            initial={{ opacity: 0, x: -24 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="relative"
          >
            {/* Decorative accent frame */}
            <div
              className="absolute -top-3 -left-3 w-[88%] h-[88%] rounded-2xl pointer-events-none hidden sm:block"
              style={{ border: `3px solid ${ACCENT}` }}
            />

            <div className="relative rounded-2xl overflow-hidden shadow-xl">
              <img
                src={whyUsImg}
                alt="AST Constructions site team overseeing a building project"
                loading="lazy"
                className="w-full h-full object-cover aspect-4/3"
              />
            </div>

            {/* Floating experience badge */}
            <div
              className="absolute bottom-3 left-3 rounded-xl px-3 py-2.5 shadow-xl max-w-50"
              style={{ backgroundColor: NAVY, border: `1px solid rgba(255,170,0,0.3)` }}
            >
              <div className="flex items-center gap-2 mb-0.5">
                <HardHat className="w-4 h-4 shrink-0" style={{ color: ACCENT }} />
                <span className="text-lg font-black text-white leading-none">12+</span>
              </div>
              <p className="text-white text-[10px] font-bold uppercase tracking-wide leading-tight">Years of Experience</p>
              <p className="text-[10px] mt-0.5 leading-snug" style={{ color: '#9FB0C7' }}>Trusted construction expertise</p>
            </div>
          </motion.div>

          {/* ── Right — Content ── */}
          <div>
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="flex items-center gap-2.5 mb-2"
            >
              <span className="w-6 h-px" style={{ backgroundColor: ACCENT }} />
              <span className="text-[10px] font-bold uppercase tracking-[0.2em]" style={{ color: ACCENT }}>Why Choose AST?</span>
            </motion.div>

            <motion.h2
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.05 }}
              viewport={{ once: true }}
              className="text-2xl sm:text-3xl font-black leading-tight mb-2.5"
            >
              <span style={{ color: '#FFFFFF' }}>Built on experience.</span>{' '}
              <span style={{ color: ACCENT }}>Driven by quality.</span>
            </motion.h2>

            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
              className="leading-relaxed mb-4 max-w-xl text-[13px]"
              style={{ color: BODY }}
            >
              At AST Constructions, we bring together experienced professionals, quality-focused workmanship
              and disciplined project management to deliver reliable construction solutions.
            </motion.p>

            {/* Feature cards */}
            <div className="grid sm:grid-cols-2 gap-2.5 mb-3.5">
              {features.map((feature, index) => (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.06 }}
                  viewport={{ once: true }}
                  className="rounded-xl p-3 flex items-start gap-2.5 transition-colors duration-300 hover:border-amber-400/40"
                  style={{ backgroundColor: CARD_BG, border: `1px solid ${BORDER}` }}
                >
                  <div
                    className="w-8 h-8 rounded-full flex items-center justify-center shrink-0"
                    style={{ backgroundColor: 'rgba(255,170,0,0.15)' }}
                  >
                    <feature.icon className="w-4 h-4" style={{ color: ACCENT }} />
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-bold text-[13px] leading-tight mb-0.5 text-white">{feature.title}</h3>
                    <p className="text-[11.5px] leading-snug" style={{ color: BODY }}>{feature.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Trust strip */}
            <div
              className="flex flex-wrap items-center gap-x-5 gap-y-1.5 rounded-lg px-4 py-2.5 mb-4"
              style={{ backgroundColor: 'rgba(255,255,255,0.06)' }}
            >
              {trustStrip.map((item) => (
                <span key={item} className="flex items-center gap-1.5 text-xs font-semibold text-white">
                  <CheckCircle2 className="w-3.5 h-3.5 shrink-0" style={{ color: ACCENT }} />
                  {item}
                </span>
              ))}
            </div>

            <Link to="/about" className="block sm:inline-block">
              <button
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 font-bold text-xs px-5 py-2.5 rounded-lg transition-transform hover:-translate-y-0.5"
                style={{ backgroundColor: ACCENT, color: NAVY }}
              >
                Learn More About AST
                <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
